import { XIcon } from '@phosphor-icons/react'

import { Button } from '../button'
import { Icon } from '../icon'
import * as S from './table.styled'
import { BulkAction } from './table.types'

export interface TableBulkMenuProps<T> {
  selectedCount: number
  bulkActions?: BulkAction<T>[]
  selectedItems: T[]
  onClearSelection: () => void
}

interface BulkActionItemProps<T> {
  action: BulkAction<T>
  selectedItems: T[]
  onAction: (action: BulkAction<T>) => void
}

const BulkActionItem = <T,>({ action, selectedItems, onAction }: BulkActionItemProps<T>) => {
  const isDisabled = action.disabled?.(selectedItems) ?? false
  const tooltipText = isDisabled ? action.tooltip?.(selectedItems) : undefined

  const button = (
    <Button
      use={action.use ?? 'primary-light'}
      size="small"
      onlyBox={action.onlyBox}
      disabled={isDisabled}
      onClick={() => onAction(action)}
    >
      {action.icon && <Icon icon={action.icon} size="1.6rem" color={action.iconColor} />}
      {action.label}
    </Button>
  )

  if (tooltipText) {
    return (
      <S.BulkActionTooltip trigger={button} placement="top-end" offsetValue={22}>
        {tooltipText}
      </S.BulkActionTooltip>
    )
  }

  return button
}

export const TableBulkMenu = <T,>({
  selectedCount,
  bulkActions,
  selectedItems,
  onClearSelection,
}: TableBulkMenuProps<T>) => {
  const handleBulkAction = (action: BulkAction<T>) => {
    action.onClick(selectedItems)
  }

  const filteredBulkActions = bulkActions?.filter((action) => !action.hidden?.(selectedItems)) ?? []

  return (
    <S.BulkActionsWrapper>
      <S.BulkActionsBar>
        <S.BulkClearButton type="button" title="Clear selection" onClick={onClearSelection}>
          <Icon icon={XIcon} size="1.6rem" />
        </S.BulkClearButton>
        <S.BulkSelectionInfo>{selectedCount} selected</S.BulkSelectionInfo>
        {filteredBulkActions.map((action, index) => (
          <BulkActionItem
            key={action.label ?? index}
            action={action}
            selectedItems={selectedItems}
            onAction={handleBulkAction}
          />
        ))}
      </S.BulkActionsBar>
    </S.BulkActionsWrapper>
  )
}
